import { Box } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';

function looksLikeHtml(str) {
  return /<\/?[a-z][\s\S]*?>/i.test(str);
}

/** Markdown Strapi parfois enveloppé dans un ou plusieurs `<p>…</p>`. */
function strapiRichtextToMarkdownInput(html) {
  const t = html.trim();
  if (!/<p[\s>]/i.test(t)) return t;
  return t
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
    .replace(/^<p[^>]*>/i, '')
    .replace(/<\/p>\s*$/i, '')
    .trim();
}

function shouldRenderAsHtml(str) {
  if (!looksLikeHtml(str)) return false;
  if (/\*\*[\s\S]+?\*\*/.test(str)) return false;
  return true;
}

const markdownLink = ({ href, children, ...props }) => (
  <a
    href={href}
    {...props}
    {...(href && /^https?:\/\//i.test(href) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
  >
    {children}
  </a>
);

/** Styles de base alignés sur les pages éditoriales du site (surcharge via `sx`). */
export const strapiRichTextBaseSx = {
  fontFamily: 'Montserrat',
  lineHeight: 1.8,
  fontWeight: 500,
  color: '#2f3036',
  '& p': { marginTop: 0, marginBottom: '1rem' },
  '& p:last-child': { marginBottom: 0 },
  '& strong': { fontWeight: 700 },
  '& a': { color: '#1398aa' },
  /* Strapi Markdown + HTML mixte (ex. <u> depuis l’éditeur) */
  '& u': { textDecoration: 'underline', fontStyle: 'italic' },
};

/**
 * Affiche un champ richtext Strapi 4 (Markdown ou HTML pur).
 * - Markdown : **gras**, liens, sauts de ligne (remark-breaks) + HTML inline (rehype-raw, ex. <u>)
 * - HTML sans `**` : injection directe (anciennes entrées)
 */
export default function StrapiRichText({ source, sx = {}, component = 'div', ...boxProps }) {
  const baseSx = { ...strapiRichTextBaseSx, ...sx };
  const trimmed = String(source ?? '').trim();

  if (!trimmed) {
    return null;
  }

  if (shouldRenderAsHtml(trimmed)) {
    return (
      <Box component={component} sx={baseSx} dangerouslySetInnerHTML={{ __html: trimmed }} {...boxProps} />
    );
  }

  const markdownSource = looksLikeHtml(trimmed) ? strapiRichtextToMarkdownInput(trimmed) : trimmed;

  return (
    <Box component={component} sx={baseSx} {...boxProps}>
      <ReactMarkdown
        remarkPlugins={[remarkBreaks]}
        rehypePlugins={[rehypeRaw]}
        components={{ a: markdownLink }}
      >
        {markdownSource}
      </ReactMarkdown>
    </Box>
  );
}
